/**
 * Our Journey Section frontend: image-only Splide slider, fading text, year progress bar.
 */
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';

import {
	CONTENT_FADE_OUT_MS,
	applyAdaptiveHeights,
	isMobileLayout,
	measureAndApplyHeights,
	resolveSlideIndex,
	scheduleProgressUpdate,
	updateProgressBar,
} from './slider-utils';

( function () {
	'use strict';

	/**
	 * Remove Splide UI we do not use.
	 *
	 * @param {HTMLElement} root Slider root element.
	 */
	function cleanupSplideChrome( root ) {
		root.querySelectorAll( '.splide__pagination, .splide__arrows' ).forEach(
			( element ) => element.remove()
		);

		root.querySelectorAll( '.splide__sr' ).forEach( ( element ) => {
			element.setAttribute( 'aria-hidden', 'true' );
		} );
	}

	/**
	 * Scroll the active progress point into view on mobile.
	 *
	 * @param {HTMLElement} section     Section root element.
	 * @param {number}      activeIndex Active slide index.
	 */
	function scrollActiveProgressPoint( section, activeIndex ) {
		const scrollContainer = section.querySelector(
			'.alteia-our-journey-section__progress-scroll'
		);
		const activePoint = section.querySelector(
			`[data-journey-year="${ activeIndex }"]`
		);

		if ( ! scrollContainer || ! activePoint ) {
			return;
		}

		const containerRect = scrollContainer.getBoundingClientRect();
		const pointRect = activePoint.getBoundingClientRect();
		const offset =
			pointRect.left -
			containerRect.left -
			containerRect.width / 2 +
			pointRect.width / 2;

		scrollContainer.scrollTo( {
			left: scrollContainer.scrollLeft + offset,
			behavior: 'smooth',
		} );
	}

	/**
	 * Create a content panel switcher with sequential fade-out / fade-in timing.
	 *
	 * @param {HTMLElement} section Section root element.
	 * @return {Function} Panel switch handler.
	 */
	function createContentPanelSwitcher( section ) {
		const panels = section.querySelectorAll(
			'[data-journey-content-panel]'
		);
		const prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;
		const fadeOutMs =
			prefersReducedMotion || isMobileLayout() ? 0 : CONTENT_FADE_OUT_MS;
		const panelClass = {
			active: 'alteia-our-journey-section__content-panel--active',
			leaving: 'alteia-our-journey-section__content-panel--leaving',
		};

		let currentIndex = 0;
		let switchTimer = null;
		let isTransitioning = false;

		/**
		 * Apply the final visible panel state without animation.
		 *
		 * @param {number} nextIndex Target panel index.
		 */
		function applyPanelState( nextIndex ) {
			panels.forEach( ( panel, index ) => {
				const isActive = index === nextIndex;

				panel.classList.remove( panelClass.active, panelClass.leaving );
				panel.classList.toggle( panelClass.active, isActive );
				panel.hidden = ! isActive;
				panel.setAttribute(
					'aria-hidden',
					isActive ? 'false' : 'true'
				);
			} );

			currentIndex = nextIndex;
			isTransitioning = false;
		}

		/**
		 * @param {number} nextIndex Target panel index.
		 */
		return function switchContentPanel( nextIndex ) {
			const nextPanel = panels[ nextIndex ];

			if ( ! nextPanel ) {
				return;
			}

			if (
				nextIndex === currentIndex &&
				! isTransitioning &&
				nextPanel.classList.contains( panelClass.active )
			) {
				return;
			}

			if ( switchTimer ) {
				window.clearTimeout( switchTimer );
				switchTimer = null;
			}

			const currentPanel = panels[ currentIndex ];

			if (
				fadeOutMs === 0 ||
				! currentPanel ||
				currentPanel === nextPanel
			) {
				applyPanelState( nextIndex );
				return;
			}

			isTransitioning = true;

			panels.forEach( ( panel ) => {
				if ( panel !== currentPanel ) {
					panel.classList.remove(
						panelClass.active,
						panelClass.leaving
					);
					panel.hidden = true;
					panel.setAttribute( 'aria-hidden', 'true' );
				}
			} );

			currentPanel.classList.remove( panelClass.active );
			currentPanel.classList.add( panelClass.leaving );

			switchTimer = window.setTimeout( () => {
				currentPanel.classList.remove( panelClass.leaving );
				currentPanel.hidden = true;
				currentPanel.setAttribute( 'aria-hidden', 'true' );

				nextPanel.hidden = false;
				nextPanel.setAttribute( 'aria-hidden', 'false' );

				void nextPanel.offsetHeight;

				nextPanel.classList.add( panelClass.active );

				currentIndex = nextIndex;
				isTransitioning = false;
				switchTimer = null;
			}, fadeOutMs );
		};
	}

	/**
	 * Wait for images to finish loading, with a timeout per image.
	 *
	 * @param {Array<HTMLImageElement>} images    Image elements.
	 * @param {number}                  [maxWait] Max wait per image in ms.
	 * @return {Promise<void>} Resolves when images load, error, or timeout.
	 */
	function waitForImages( images, maxWait = 2500 ) {
		return Promise.all(
			Array.from( images ).map( ( image ) => {
				if ( image.complete ) {
					return Promise.resolve();
				}

				return Promise.race( [
					new Promise( ( resolve ) => {
						image.addEventListener( 'load', resolve, {
							once: true,
						} );
						image.addEventListener( 'error', resolve, {
							once: true,
						} );
					} ),
					new Promise( ( resolve ) => {
						window.setTimeout( resolve, maxWait );
					} ),
				] );
			} )
		);
	}

	/**
	 * Initialize one Our Journey section instance.
	 *
	 * @param {HTMLElement} section Section root element.
	 */
	function initJourneySection( section ) {
		const sliderRoot = section.querySelector( '[data-journey-slider]' );

		if ( ! sliderRoot ) {
			return;
		}

		const autoplay = section.dataset.journeyAutoplay === 'true';
		const interval =
			parseInt( section.dataset.journeyInterval, 10 ) || 5000;
		const loop = section.dataset.journeyLoop !== 'false';
		const slideCount =
			sliderRoot.querySelectorAll(
				'.splide__slide:not(.splide__slide--clone)'
			).length || sliderRoot.querySelectorAll( '.splide__slide' ).length;

		if ( slideCount <= 0 ) {
			return;
		}

		updateProgressBar( 0, section );

		const heightOptions = () => ( {
			setContentHeight: ! isMobileLayout(),
		} );

		let contentHeights = [];
		let imageHeights = [];

		const applyHeightsForIndex = ( activeIndex ) => {
			if ( ! contentHeights.length || ! imageHeights.length ) {
				return;
			}

			applyAdaptiveHeights(
				section,
				sliderRoot,
				activeIndex,
				contentHeights,
				imageHeights,
				heightOptions()
			);
		};

		const measureHeights = ( activeIndex = 0 ) => {
			const measured = measureAndApplyHeights(
				section,
				sliderRoot,
				activeIndex,
				heightOptions()
			);

			contentHeights = measured.contentHeights;
			imageHeights = measured.imageHeights;
		};

		measureHeights( 0 );

		const splide = new Splide( sliderRoot, {
			type: loop ? 'loop' : 'slide',
			direction:
				document.documentElement.getAttribute( 'dir' ) === 'rtl'
					? 'rtl'
					: 'ltr',
			perPage: 1,
			perMove: 1,
			gap: 0,
			padding: 0,
			pagination: false,
			arrows: false,
			drag: slideCount > 1,
			speed: 600,
			rewind: ! loop,
			autoHeight: false,
			autoplay,
			interval,
			pauseOnHover: true,
			pauseOnFocus: true,
			reduceMotion: {
				speed: 0,
				rewindSpeed: 0,
				autoplay: false,
			},
		} );

		const switchContentPanel = createContentPanelSwitcher( section );

		const syncSlideState = ( destIndex ) => {
			const activeIndex = resolveSlideIndex( splide, destIndex );

			switchContentPanel( activeIndex );
			applyHeightsForIndex( activeIndex );
			scheduleProgressUpdate( activeIndex, section );

			window.requestAnimationFrame( () => {
				scrollActiveProgressPoint( section, activeIndex );
			} );
		};

		const stabilizeLayout = async () => {
			const images = sliderRoot.querySelectorAll(
				'.splide__slide:not(.splide__slide--clone) .alteia-our-journey-section__slide-image'
			);

			await waitForImages( images );

			const measured = measureAndApplyHeights(
				section,
				sliderRoot,
				resolveSlideIndex( splide ),
				heightOptions()
			);

			contentHeights = measured.contentHeights;
			imageHeights = measured.imageHeights;
			scheduleProgressUpdate( resolveSlideIndex( splide ), section );
		};

		splide.on( 'mounted', () => {
			cleanupSplideChrome( sliderRoot );
			stabilizeLayout().then( () => {
				syncSlideState( undefined );
				section.classList.add( 'is-journey-ready' );
			} );
		} );

		splide.on( 'move', ( index, prev, dest ) => {
			syncSlideState( dest );
		} );

		splide.mount();
		cleanupSplideChrome( sliderRoot );

		let resizeTimer;

		window.addEventListener( 'resize', () => {
			window.clearTimeout( resizeTimer );
			resizeTimer = window.setTimeout( () => {
				stabilizeLayout();
			}, 150 );
		} );

		section.addEventListener( 'click', ( event ) => {
			if ( event.target.closest( '[data-journey-prev]' ) ) {
				event.preventDefault();
				splide.go( '<' );
				return;
			}

			if ( event.target.closest( '[data-journey-next]' ) ) {
				event.preventDefault();
				splide.go( '>' );
				return;
			}

			const yearButton = event.target.closest( '[data-journey-year]' );

			if ( yearButton ) {
				event.preventDefault();
				const targetIndex = parseInt(
					yearButton.getAttribute( 'data-journey-year' ),
					10
				);

				if ( ! Number.isNaN( targetIndex ) ) {
					splide.go( targetIndex );
				}
			}
		} );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll( '.alteia-our-journey-section' )
			.forEach( initJourneySection );
	} );
} )();
